<script setup lang="ts">
import type { Coupon } from '#shared/types/coupon'
import type { PaginatedResponse } from '#shared/types/api'

// ISR via routeRules: '/cupons' → isr: 600
definePageMeta({ layout: 'default' })

useSeoMeta({
  title: 'Cupons de Desconto — Descontito',
  description: 'Os melhores cupons de desconto para as maiores lojas do Brasil.',
})

const config = useRuntimeConfig()
const { trackEvent } = useUmami()

const { data, status } = await useFetch<PaginatedResponse<Coupon>>('/coupons', {
  baseURL: config.public.apiBaseUrl,
  query: { limit: 50, status: 'ACTIVE' },
  key: 'coupons-list',
})

const copiedCode = ref<string | null>(null)

function copyCode(coupon: Coupon) {
  navigator.clipboard.writeText(coupon.code)
  copiedCode.value = coupon.id
  setTimeout(() => { copiedCode.value = null }, 2000)

  trackEvent('coupon-copy', { couponId: coupon.id, store: coupon.store.name, code: coupon.code })
}
</script>

<template>
  <div>
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Cupons de Desconto</h1>
    <p class="text-gray-500 mb-6">Copie o código e use na hora de finalizar sua compra.</p>

    <!-- Skeleton -->
    <div v-if="status === 'pending'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <USkeleton v-for="i in 9" :key="i" class="h-36 rounded-xl" />
    </div>

    <!-- Lista de cupons -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <UCard v-for="coupon in data?.items" :key="coupon.id" class="hover:shadow-md transition-shadow">
        <div class="flex items-start gap-3">
          <NuxtImg
            v-if="coupon.store.logoUrl"
            :src="coupon.store.logoUrl"
            :alt="coupon.store.name"
            class="w-10 h-10 object-contain rounded border border-gray-200 dark:border-gray-700 p-1 shrink-0"
          />
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-sm text-gray-900 dark:text-white">{{ coupon.store.name }}</p>
            <p v-if="coupon.description" class="text-xs text-gray-500 mt-0.5 line-clamp-2">{{ coupon.description }}</p>
            <p v-if="coupon.discount" class="text-sm font-bold text-primary-500 mt-1">{{ coupon.discount }}</p>
          </div>
        </div>

        <div class="mt-3 flex items-center gap-2">
          <code class="flex-1 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded text-sm font-mono tracking-widest text-center border border-dashed border-gray-300 dark:border-gray-600">
            {{ coupon.code }}
          </code>
          <UButton
            :icon="copiedCode === coupon.id ? 'i-heroicons-check' : 'i-heroicons-clipboard'"
            :color="copiedCode === coupon.id ? 'success' : 'primary'"
            size="sm"
            @click="copyCode(coupon)"
          >
            {{ copiedCode === coupon.id ? 'Copiado!' : 'Copiar' }}
          </UButton>
        </div>

        <div v-if="coupon.expiresAt" class="mt-2 text-xs text-gray-400">
          Válido até {{ new Date(coupon.expiresAt).toLocaleDateString('pt-BR') }}
        </div>

        <UBadge v-if="coupon.isVerified" color="success" variant="soft" size="xs" class="mt-2">
          ✓ Verificado
        </UBadge>
      </UCard>
    </div>

    <!-- Empty state -->
    <div v-if="!status && !data?.items?.length" class="text-center py-16 text-gray-400">
      <UIcon name="i-heroicons-ticket" class="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>Nenhum cupom disponível no momento.</p>
    </div>
  </div>
</template>
